import { useQuery, UseQueryOptions } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { FulfilmentClient } from '@jbhi-fi/apis/src/fulfilment';
import {
  RequiredFulfilmentOption,
  FulfilmentOptionsLineItem,
  FulfilmentOptionsResponse,
} from '@jbhi-fi/apis/src/fulfilment/types';
import { useRecaptchaContext } from '../../context/RecaptchaProvider';
import { FULFILMENT_API_RECAPTCHA_ACTION } from '@jbhi-fi/apis/src/fulfilment/FulfilmentClient';
import { CountryCode } from '@jbhi-fi/apis/src/utils/multiTenancyHeaders';

export type UseFulfilmentOptionsResult = {
  data?: Array<FulfilmentOptionsResponse>;
  isLoading: boolean;
  error?: unknown;
};

type Address = {
  countryCode: CountryCode;
  postcode: string;
  suburb: string;
  requestTypes: Array<RequiredFulfilmentOption>;
};

type UseFulfilmentOptionsProps = {
  fulfilmentClient?: FulfilmentClient;
  isEnabled?: boolean;
  addresses: Array<Address | null>;
  lineItems: Array<FulfilmentOptionsLineItem>;
  requiresScheduledOptions?: boolean;
} & Pick<UseQueryOptions, 'cacheTime' | 'staleTime'>;

/**
 * Consolidates addresses that are identical
 * Removes duplicates and combines request types
 */
const getConsolidatedAddresses = (addresses: Array<Address | null>): Array<Address> => {
  const output: Array<Address> = [];

  for (const address of addresses) {
    if (!address) continue;

    const matchingAddress: Address | undefined = output.find(
      a => a.countryCode === address.countryCode && a.postcode === address.postcode && a.suburb === address.suburb
    );
    if (matchingAddress) {
      matchingAddress.requestTypes = Array.from(
        new Set([...(matchingAddress.requestTypes ?? []), ...(address.requestTypes ?? [])])
      );
      continue;
    }

    output.push(address);
  }

  return output;
};

export const useFulfilmentOptions = ({
  fulfilmentClient: fulfilmentClientProp,
  addresses,
  lineItems,
  isEnabled,
  requiresScheduledOptions = true,
  ...useQueryOptions
}: UseFulfilmentOptionsProps): UseFulfilmentOptionsResult => {
  const { fulfilmentClient: fulfilmentClientContext } = usePublicApiClient();
  const fulfilmentClient = fulfilmentClientProp || fulfilmentClientContext;
  const { generateToken, isReady: isRecaptchaReady } = useRecaptchaContext();

  if (!fulfilmentClient) {
    throw new Error('useFulfilmentOptions must be used with a contentfulThemeApiClient');
  }

  const consolidatedAddresses = getConsolidatedAddresses(addresses);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'fulfilmentOptions',
      consolidatedAddresses.map(address => {
        return {
          countryCode: address.countryCode,
          postcode: address.postcode,
          suburb: address.suburb,
        };
      }),
      lineItems.map(lineItem => ({
        sku: lineItem.SKU ?? '',
        quantity: lineItem.Quantity ?? 1,
        lineItemFreightPrice: lineItem.LineItemFreightPrice ?? 0,
        giftCardType: lineItem.GiftCardType ?? null,
        tieredIds: lineItem.TieredIds,
      })),
      { requiresScheduledOptions },
    ],
    queryFn: async () => {
      const recaptchaToken = await generateToken?.(FULFILMENT_API_RECAPTCHA_ACTION);

      const response = await fulfilmentClient?.getMultipleOptions(
        consolidatedAddresses.map((address: Address) => {
          return {
            Address: {
              CountryCode: address.countryCode,
              Postcode: address.postcode,
              Suburb: address.suburb,
            },
            LineItems: lineItems,
            requiresScheduledOptions,
            RequiredFulfilmentOptions: address.requestTypes,
          };
        }),
        recaptchaToken ?? ''
      );

      if (response.error || !response?.data) {
        throw `Failed to fetch availability: ${response?.error?.message} ${response?.statusCode}`;
      }

      return response;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    enabled: isEnabled && isRecaptchaReady && !!lineItems?.length && addresses.some(a => !!a?.postcode),
    ...useQueryOptions,
  });

  const reconstitutedData =
    consolidatedAddresses.length === 1 && data?.data ? Array(addresses.length).fill(data?.data[0]) : data?.data;

  return { isLoading: isLoading || !isRecaptchaReady, data: reconstitutedData, error };
};
